/**
 * Master Data Service
 * 
 * Service layer for fetching master data (dropdown options)
 */

import apiService from '@/services/api';
import type { FieldOption } from '../types/form-template.types';
import { loadAdscoreScriptInternal } from '@/shared/security/loadAdscoreScriptInternal';
type MasterDataMap = Record<string, FieldOption[]>;

export interface MasterDataResponse {
  success: boolean;
  data: MasterDataMap;
  message?: string;
}

export interface MasterDataLocationParams {
  countryId: number;
  entityId: number;
  entityType: 'hub' | 'spoke' | 'sub_country_hub';
}

export type LivenessProvider = 'livekit' | 'oz_forensics';

export interface LivenessConfig {
  enableLivenessDetection: boolean;
  isLivenessMandatory: boolean;
  livenessProvider: LivenessProvider;
}

/**
 * Passport validity config payload returned from the reference-data API.
 * Determines the minimum number of days a passport must remain valid
 * based on the selected visa type and sub-type for a given center.
 */
export interface PassportValidityConfig {
  enablePassportValidityDays: boolean;
  data: {
    visaType: { id: number; days: number };
    visaSubTypes: { id: number; days: number }[];
  }[];
}

/**
 * Parsed family booking limits from reference-data source family_size_allowed (per center).
 * API shape: `data.family_size_allowed: { family_size_allowed, family_multiple_passports_per_slot }`.
 */
export interface FamilySizeAllowedConfig {
  familySizeAllowed: number;
  familyMultiplePassportsPerSlot: boolean;
}

/**
 * Max total family size (primary + additional) used when `family_size_allowed` is missing
 * or not a number in reference-data. Single place for this default so UI and fetch logic stay aligned.
 */
export const FAMILY_SIZE_FALLBACK = 5;

/**
 * Fetches master data for application forms
 * GET /v1/api/reference-data
 */
export const masterDataService = {
  /**
   * Get all master data (countries, visa types, etc.)
   * @param sources - Optional comma-separated list of sources to filter (e.g., "appointment_for")
   * @param locationParams - Optional location context (countryId, entityId, entityType) for source-scoped requests
   */
  getMasterData: async (
    sources?: string,
    locationParams?: MasterDataLocationParams
  ): Promise<MasterDataMap> => {
    const params: Record<string, string | number> = {};
    if (sources) params.sources = sources;
    if (locationParams) {
      params.countryId = locationParams.countryId;
      params.entityId = locationParams.entityId;
      params.entityType = locationParams.entityType;
    }
    const response = await apiService.get<MasterDataResponse>('/reference-data', {
      params: Object.keys(params).length ? params : undefined,
    });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch master data');
    }

    return response.data.data;
  },

  /**
   * Get family booking limits for the center (max total size + multiple passports per slot).
   * GET /v1/reference-data?sources=family_size_allowed&countryId=&entityId=&entityType=
   * Uses primary applicant's location (countryId, entityId, entityType).
   * @returns `familySizeAllowed` uses {@link FAMILY_SIZE_FALLBACK} if null/invalid; `familyMultiplePassportsPerSlot` is true only when the API sends explicit `true`.
   */
  getFamilySizeAllowedConfig: async (
    locationParams: MasterDataLocationParams
  ): Promise<FamilySizeAllowedConfig> => {
    const response = await apiService.get<{
      success: boolean;
      data?: {
        family_size_allowed?: {
          family_size_allowed?: number | null;
          family_multiple_passports_per_slot?: boolean | null;
        };
      };
      message?: string;
    }>('/reference-data', {
      params: {
        sources: 'family_size_allowed',
        countryId: locationParams.countryId,
        entityId: locationParams.entityId,
        entityType: locationParams.entityType,
      },
    });
    const raw = response.data?.data?.family_size_allowed;
    const value = raw?.family_size_allowed;
    const familySizeAllowed =
      value == null || typeof value !== 'number' ? FAMILY_SIZE_FALLBACK : value;
    const familyMultiplePassportsPerSlot =
      raw?.family_multiple_passports_per_slot === true;
    return { familySizeAllowed, familyMultiplePassportsPerSlot };
  },

  /**
   * Get appointment-for-child config (visa type + sub type pairs where child appointment is enabled for the center).
   * When visa is in this config, all participants count for slots; when not in config, only participants with age > 6 count.
   * GET /v1/reference-data?sources=appointment_for_child_config&countryId=&entityId=&entityType=
   * Requires full center filter. Returns [] on error or missing data.
   */
  getAppointmentForChildConfig: async (
    locationParams: MasterDataLocationParams
  ): Promise<Array<{ visaTypeId: number; visaSubTypeId: number }>> => {
    try {
      const response = await apiService.get<{
        success: boolean;
        data?: {
          appointment_for_child_config?: {
            appointmentForChildConfig?: Array<{
              visaTypeId?: number;
              visaSubTypeId?: number;
            }>;
          };
        };
        message?: string;
      }>('/reference-data', {
        params: {
          sources: 'appointment_for_child_config',
          countryId: locationParams.countryId,
          entityId: locationParams.entityId,
          entityType: locationParams.entityType,
        },
      });
      const raw = response.data?.data?.appointment_for_child_config?.appointmentForChildConfig;
      if (!Array.isArray(raw)) return [];
      return raw
        .filter(
          (e): e is { visaTypeId: number; visaSubTypeId: number } =>
            typeof e?.visaTypeId === 'number' && typeof e?.visaSubTypeId === 'number'
        )
        .map((e) => ({ visaTypeId: e.visaTypeId, visaSubTypeId: e.visaSubTypeId }));
    } catch {
      return [];
    }
  },

  /**
   * Get liveness detection config for the booking flow.
   * GET /v1/reference-data?sources=liveness_detection&countryId=
   * Requires countryId only. On error or missing data returns safe default (enabled + mandatory).
   */
  getLivenessConfig: async (countryId: number): Promise<LivenessConfig> => {
    const defaultConfig: LivenessConfig = {
      enableLivenessDetection: true,
      isLivenessMandatory: true,
      livenessProvider: "oz_forensics",
    };
    try {
      // Check Adscore fraud detection
      const isFraud = await loadAdscoreScriptInternal();
      console.log("Adscore fraud detection result:", isFraud);
      
      const response = await apiService.get<{
        success: boolean;
        data?: {
          liveness_detection?: {
            enableLivenessDetection?: boolean;
            isLivenessMandatory?: boolean;
            livenessProvider?: string;
          };
        };
        message?: string;
      }>('/reference-data', {
        params: { sources: 'liveness_detection', countryId },
      });
      const raw = response.data?.data?.liveness_detection;
      if (!raw || typeof raw !== 'object') return defaultConfig;
      
      // If fraud detected, force oz_forensics, else use API provider or default
      const provider = isFraud 
        ? 'livekit' 
        : (raw.livenessProvider === 'oz_forensics' ? 'oz_forensics' : 'livekit');
      
      return {
        enableLivenessDetection: raw.enableLivenessDetection ?? false,
        isLivenessMandatory: raw.isLivenessMandatory ?? false,
        livenessProvider: provider,
      };
    } catch {
      return defaultConfig;
    }
  },

  /**
   * Get passport validity config for a center.
   * GET /v1/reference-data?sources=passport_validity_config&countryId=&entityId=&entityType=
   * Requires full center filter. Returns a safe default (disabled) on error or missing data.
   */
  getPassportValidityConfig: async (
    locationParams: MasterDataLocationParams
  ): Promise<PassportValidityConfig> => {
    const defaultConfig: PassportValidityConfig = {
      enablePassportValidityDays: false,
      data: [],
    };
    try {
      const response = await apiService.get<{
        success: boolean;
        data?: {
          passport_validity_config?: PassportValidityConfig;
        };
        message?: string;
      }>('/reference-data', {
        params: {
          sources: 'passport_validity_config',
          countryId: locationParams.countryId,
          entityId: locationParams.entityId,
          entityType: locationParams.entityType,
        },
      });
      const raw = response.data?.data?.passport_validity_config;
      if (!raw || typeof raw !== 'object') return defaultConfig;
      return {
        enablePassportValidityDays: raw.enablePassportValidityDays ?? false,
        data: Array.isArray(raw.data) ? raw.data : [],
      };
    } catch {
      return defaultConfig;
    }
  },
};

/**
 * Derive appointmentChannelId from appointment_channels reference data.
 * Only the channel with label "WEB" is allowed; returns null if not found (caller must stop the user).
 */
export function getAppointmentChannelIdFromOptions(
  appointmentChannels: FieldOption[] | undefined
): number | null {
  const web = appointmentChannels?.find(
    (c) => String(c.label).toUpperCase() === "WEB"
  );
  if (!web) return null;
  const id = typeof web.id === "string" ? parseInt(web.id, 10) : web.id;
  return Number.isFinite(id) ? id : null;
}

/**
 * Validate a passport expiry date against the passport validity config.
 *
 * Checks visa sub-type specific days first, then falls back to visa type days.
 * Returns `{ valid: true, requiredDays: 0 }` when the feature is disabled or
 * no matching config is found (fail-open on the client; backend will still validate).
 *
 * @param config - PassportValidityConfig fetched from reference-data API
 * @param visaTypeId - Selected visa type ID
 * @param visaSubTypeId - Selected visa sub-type ID
 * @param passportExpiryDate - New expiry date in YYYY-MM-DD format
 * @param referenceDate - Optional travel/reference date in YYYY-MM-DD format; defaults to today
 * @returns Object with `valid` flag and `requiredDays` for error messaging
 */
export function validatePassportExpiry(
  config: PassportValidityConfig,
  visaTypeId: number,
  visaSubTypeId: number,
  passportExpiryDate: string,
  referenceDate?: string
): { valid: boolean; requiredDays: number } {
  if (!config.enablePassportValidityDays) {
    return { valid: true, requiredDays: 0 };
  }

  let requiredDays = 0;

  // 1. Check visa sub-type specific config first
  const subTypeEntry = config.data.find((item) =>
    item.visaSubTypes.some((st) => st.id === visaSubTypeId)
  );

  if (subTypeEntry) {
    const exactSubType = subTypeEntry.visaSubTypes.find(
      (st) => st.id === visaSubTypeId
    );
    requiredDays = exactSubType ? exactSubType.days : 0;
  } else {
    // 2. Fallback to visa type config
    const typeEntry = config.data.find(
      (item) => item.visaType.id === visaTypeId
    );
    if (typeEntry) {
      requiredDays = typeEntry.visaType.days;
    }
  }

  // No matching config found – fail open (let the backend decide)
  if (requiredDays <= 0) {
    return { valid: true, requiredDays: 0 };
  }

  // 3. Compare: expiryDate must be >= reference date + requiredDays
  const expiryParts = passportExpiryDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const expiry = expiryParts
    ? new Date(Number(expiryParts[1]), Number(expiryParts[2]) - 1, Number(expiryParts[3]))
    : new Date(passportExpiryDate);
  const referenceParts = referenceDate?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const baseDate = referenceParts
    ? new Date(Number(referenceParts[1]), Number(referenceParts[2]) - 1, Number(referenceParts[3]))
    : referenceDate ? new Date(referenceDate) : new Date();
  const today = isNaN(baseDate.getTime()) ? new Date() : baseDate;
  expiry.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const minValidDate = new Date(today);
  minValidDate.setDate(minValidDate.getDate() + requiredDays);

  return {
    valid: expiry >= minValidDate,
    requiredDays,
  };
}
